import React from 'react';
import { Calculator, Percent, ArrowRightLeft } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров рефинансирования
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  setErrors,
  isCalculating
}) => {
  // Расчет текущей суммы кредита
  const currentLoanAmount = parseFloat(formData.currentLoanAmount) || 0;
  
  // Расчет дополнительной суммы
  const additionalAmount = parseFloat(formData.additionalAmount) || 0;
  
  // Расчет текущей ставки
  const currentInterestRate = parseFloat(formData.currentInterestRate) || 0;
  
  // Расчет новой ставки
  const newInterestRate = parseFloat(formData.newInterestRate) || 0;
  
  // Обработчик изменения суммы текущего кредита
  const handleCurrentLoanAmountChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      currentLoanAmount: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.currentLoanAmount) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.currentLoanAmount;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения новой процентной ставки
  const handleNewInterestRateChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      newInterestRate: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.newInterestRate) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.newInterestRate;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения дополнительной суммы
  const handleAdditionalAmountChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      additionalAmount: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.additionalAmount) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.additionalAmount;
        return newErrors;
      });
    }
  };
  
  // Предопределенные суммы кредита для быстрого выбора
  const predefinedCurrentAmounts = [
    { value: '500000', label: '500 тыс.' },
    { value: '1000000', label: '1 млн' },
    { value: '2000000', label: '2 млн' },
    { value: '5000000', label: '5 млн' }
  ];
  
  // Предопределенные дополнительные суммы для быстрого выбора
  const predefinedAdditionalAmounts = [
    { value: '0', label: 'Без доп. суммы' },
    { value: '100000', label: '100 тыс.' },
    { value: '300000', label: '300 тыс.' },
    { value: '500000', label: '500 тыс.' }
  ];
  
  // Предопределенные процентные ставки для быстрого выбора
  const predefinedNewRates = [
    { value: '7.5', label: '7.5%' },
    { value: '8.9', label: '8.9%' },
    { value: '9.5', label: '9.5%' },
    { value: '10.9', label: '10.9%' }
  ];
  
  // Функция расчета
  const calculateRefinancing = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрый подбор параметров
      </h3>
      
      <div className="space-y-5">
        {/* Сумма текущего кредита */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="currentLoanAmountSlider" className="text-gray-600 dark:text-gray-400">Текущий остаток долга</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(currentLoanAmount, 0)}</span>
          </div>
          <input
            type="range"
            id="currentLoanAmountSlider"
            min="100000"
            max="10000000"
            step="50000"
            value={currentLoanAmount}
            onChange={handleCurrentLoanAmountChange}
            aria-label="Сумма текущего кредита"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными суммами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedCurrentAmounts.map(amount => (
              <button
                key={amount.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, currentLoanAmount: amount.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.currentLoanAmount === amount.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {amount.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Дополнительная сумма */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="additionalAmountSlider" className="text-gray-600 dark:text-gray-400">Дополнительная сумма</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(additionalAmount, 0)}</span>
          </div>
          <input
            type="range"
            id="additionalAmountSlider"
            min="0"
            max="2000000"
            step="50000"
            value={additionalAmount}
            onChange={handleAdditionalAmountChange}
            aria-label="Дополнительная сумма"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными суммами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedAdditionalAmounts.map(amount => (
              <button
                key={amount.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, additionalAmount: amount.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.additionalAmount === amount.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {amount.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Новая процентная ставка */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="newInterestRateSlider" className="text-gray-600 dark:text-gray-400">Новая процентная ставка</label>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 dark:text-white">{newInterestRate}%</span>
              {currentInterestRate > 0 && newInterestRate > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  newInterestRate < currentInterestRate 
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                    : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                }`}>
                  {newInterestRate < currentInterestRate 
                    ? `-${(currentInterestRate - newInterestRate).toFixed(1)}%` 
                    : `+${(newInterestRate - currentInterestRate).toFixed(1)}%`
                  }
                </span>
              )}
            </div>
          </div>
          <input
            type="range"
            id="newInterestRateSlider"
            min="5"
            max="20"
            step="0.1"
            value={newInterestRate}
            onChange={handleNewInterestRateChange}
            aria-label="Новая процентная ставка (%)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Предопределенные процентные ставки */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedNewRates.map(rate => (
              <button
                key={rate.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, newInterestRate: rate.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.newInterestRate === rate.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {rate.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Выбор цели рефинансирования */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Цель рефинансирования</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, optimizationGoal: 'payment' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.optimizationGoal === 'payment' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Снизить платеж</div>
              <div className="text-xs opacity-75">Уменьшить ежемесячную нагрузку</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, optimizationGoal: 'term' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.optimizationGoal === 'term' 
                  ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Сократить срок</div>
              <div className="text-xs opacity-75">Быстрее закрыть кредит</div>
            </button>
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateRefinancing}
          icon={<ArrowRightLeft className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать рефинансирование
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;